/**
 * Leviathan Web - Dispatch Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Assign orders to trucks and drivers for delivery
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface Order {
  id: string;
  orderNumber: string;
  orderType: string;
  status: string;
  total: number;
  customer: {
    name: string;
    customerNumber: string;
  };
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  items: Array<{
    product: { name: string };
    quantity: number;
    unit: string;
  }>;
}

interface Truck {
  id: string;
  unitNumber: string;
  name: string;
  totalCapacity: number;
  status: string;
}

interface Driver {
  id: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: string;
}

interface FormData {
  orderId: string;
  truckId: string;
  driverId: string;
  scheduledDate: string;
  scheduledTime: string;
  estimatedDuration: number;
  dispatchNotes: string;
}

export default function DispatchFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const [formData, setFormData] = useState<FormData>({
    orderId: searchParams.get('orderId') || '',
    truckId: '',
    driverId: '',
    scheduledDate: new Date().toISOString().split('T')[0] || '',
    scheduledTime: '08:00',
    estimatedDuration: 60,
    dispatchNotes: '',
  });

  useEffect(() => {
    fetchOrders();
    fetchTrucks();
    fetchDrivers();
    if (isEditMode) {
      fetchDispatch();
    }
  }, [id]);

  useEffect(() => {
    if (formData.orderId && orders.length > 0) {
      const order = orders.find((o) => o.id === formData.orderId);
      setSelectedOrder(order || null);
    }
  }, [formData.orderId, orders]);

  const fetchOrders = async () => {
    try {
      // Fetch orders ready for dispatch (confirmed status)
      const response = await fetch(`${API_URL}/api/orders?status=CONFIRMED&limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trucks?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const allTrucks = data.trucks || data.data || [];
        setTrucks(allTrucks.filter((t: Truck) => t.status === 'AVAILABLE' || t.status === 'IN_USE' || t.status === 'IN_YARD'));
      }
    } catch (error) {
      console.error('Failed to fetch trucks:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/drivers?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const allDrivers = data.drivers || data.data || [];
        setDrivers(
          allDrivers.filter((d: Driver) => d.status === 'AVAILABLE' || d.status === 'ON_DUTY')
        );
      }
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    }
  };

  const fetchDispatch = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dispatch/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const dispatch = data.dispatch || data;
        setFormData({
          orderId: dispatch.orderId || '',
          truckId: dispatch.truckId || '',
          driverId: dispatch.driverId || '',
          scheduledDate: dispatch.scheduledDate?.split('T')[0] || '',
          scheduledTime: dispatch.scheduledTime || '08:00',
          estimatedDuration: dispatch.estimatedDuration || 60,
          dispatchNotes: dispatch.dispatchNotes || dispatch.notes || '',
        });
      } else {
        setError('Dispatch not found');
      }
    } catch (error) {
      setError('Failed to load dispatch');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.orderId) {
      setError('Please select an order');
      return;
    }
    if (!formData.truckId) {
      setError('Please select a truck');
      return;
    }
    if (!formData.driverId) {
      setError('Please select a driver');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/dispatch/${id}` : `${API_URL}/api/dispatch`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate('/dispatch');
      } else {
        /**
         * The API failure body is { success, error: { code, message, details } }
         * — there is no top-level `message`. `data.message || data.error`
         * therefore put the error OBJECT into React state, and rendering an
         * object as a child made React unmount the whole application to a
         * white page. `readApiFailure` always returns a string, and carries
         * the per-field issues alongside it.
         */
        const failure = await readApiFailure(response, 'Failed to save dispatch');
        setError(failure.message);
      }
    } catch (error) {
      setError('Failed to save dispatch');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const timeSlots = [
    '06:00',
    '06:30',
    '07:00',
    '07:30',
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
  ];

  const formatTime = (time: string) => {
    const parts = time.split(':');
    const hours = parts[0] || '0';
    const minutes = parts[1] || '00';
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading dispatch...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Dispatch' : 'New Dispatch'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify dispatch assignment' : 'Assign an order to a truck and driver'}
              </p>
            </div>
            <button
              onClick={() => navigate('/dispatch')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Dispatch
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Order Selection */}
        <Panel title="Select Order">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Order *
            </label>
            <select
              value={formData.orderId}
              onChange={(e) => updateField('orderId', e.target.value)}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              required
              disabled={isEditMode}
            >
              <option value="">Select an order to dispatch...</option>
              {orders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.orderNumber} - {order.customer?.name || 'N/A'} ({order.orderType}) - $
                  {(order.total || 0).toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          {/* Selected Order Details */}
          {selectedOrder && (
            <div className="mt-4 p-4 bg-background border border-panel-border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-text-tertiary font-mono text-xs uppercase">Customer</div>
                  <div className="text-text-primary font-mono">
                    {selectedOrder.customer?.name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-text-tertiary font-mono text-xs uppercase">Order Type</div>
                  <div className="text-amber font-mono">{selectedOrder.orderType}</div>
                </div>
                <div>
                  <div className="text-text-tertiary font-mono text-xs uppercase">Total</div>
                  <div className="text-text-primary font-mono">
                    ${(selectedOrder.total || 0).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-text-tertiary font-mono text-xs uppercase">Products</div>
                  <div className="text-text-primary font-mono text-sm">
                    {selectedOrder.items?.map((item, i) => (
                      <div key={i}>
                        {item.quantity} {item.unit} {item.product?.name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {selectedOrder.deliveryAddress && (
                <div className="mt-4 pt-4 border-t border-panel-border">
                  <div className="text-text-tertiary font-mono text-xs uppercase mb-1">
                    Delivery Address
                  </div>
                  <div className="text-text-primary font-mono">
                    {selectedOrder.deliveryAddress}, {selectedOrder.deliveryCity},{' '}
                    {selectedOrder.deliveryState}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>

        {/* Assignment */}
        <Panel title="Assignment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Truck *
              </label>
              <select
                value={formData.truckId}
                onChange={(e) => updateField('truckId', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              >
                <option value="">Select a truck...</option>
                {trucks.map((truck) => (
                  <option key={truck.id} value={truck.id}>
                    {truck.unitNumber} {truck.name ? `(${truck.name})` : ''} -{' '}
                    {(truck.totalCapacity || 0).toLocaleString()} gal
                  </option>
                ))}
              </select>
              {trucks.length === 0 && (
                <p className="text-warning-amber font-mono text-xs mt-1">No available trucks</p>
              )}
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Driver *
              </label>
              <select
                value={formData.driverId}
                onChange={(e) => updateField('driverId', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              >
                <option value="">Select a driver...</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName} ({driver.driverNumber}){' '}
                    {driver.phone ? `- ${driver.phone}` : ''}
                  </option>
                ))}
              </select>
              {drivers.length === 0 && (
                <p className="text-warning-amber font-mono text-xs mt-1">No available drivers</p>
              )}
            </div>
          </div>
        </Panel>

        {/* Schedule */}
        <Panel title="Schedule">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Scheduled Date *
              </label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => updateField('scheduledDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Scheduled Time
              </label>
              <select
                value={formData.scheduledTime}
                onChange={(e) => updateField('scheduledTime', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {timeSlots.map((time) => (
                  <option key={time} value={time}>
                    {formatTime(time)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Est. Duration (minutes)
              </label>
              <input
                type="number"
                value={formData.estimatedDuration}
                onChange={(e) => updateField('estimatedDuration', parseInt(e.target.value) || 60)}
                min="15"
                step="15"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Dispatch Notes">
          <textarea
            value={formData.dispatchNotes}
            onChange={(e) => updateField('dispatchNotes', e.target.value)}
            rows={4}
            placeholder="Special instructions for the driver..."
            className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/dispatch')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Dispatch' : 'Create Dispatch'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
